import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../lib/api';
import { toast } from 'sonner';

export const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [catalogs, setCatalogs] = useState({ insurers: [], policy_types: [] });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    insurer: '',
    policy_type: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [plansRes, insurersRes, typesRes] = await Promise.all([
        api.get('/plans'),
        api.get('/catalogs/insurers'),
        api.get('/catalogs/policy_types')
      ]);
      setPlans(plansRes.data);
      setCatalogs({
        insurers: insurersRes.data,
        policy_types: typesRes.data
      });
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingPlan(null);
    setFormData({ name: '', insurer: '', policy_type: '' });
    setDialogOpen(true);
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      insurer: plan.insurer,
      policy_type: plan.policy_type
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.insurer || !formData.policy_type) {
      toast.error('Todos los campos son obligatorios');
      return;
    }

    try {
      if (editingPlan) {
        await api.put(`/plans/${editingPlan.id}`, formData);
        toast.success('Plan actualizado exitosamente');
      } else {
        await api.post('/plans', formData);
        toast.success('Plan agregado exitosamente');
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm('¿Estás seguro de eliminar este plan?')) return;

    try {
      await api.delete(`/plans/${planId}`);
      toast.success('Plan eliminado exitosamente');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Group plans by insurer
  const plansByInsurer = plans.reduce((acc, plan) => {
    if (!acc[plan.insurer]) {
      acc[plan.insurer] = [];
    }
    acc[plan.insurer].push(plan);
    return acc;
  }, {});

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Planes
          </h1>
          <p className="text-sm leading-relaxed text-slate-600 mt-2" style={{ fontFamily: 'Inter, sans-serif' }}>
            Gestiona los planes por aseguradora y tipo de póliza
          </p>
        </div>
        <Button
          data-testid="add-plan-button"
          onClick={handleAdd}
          className="bg-[#2537b0] hover:bg-[#1e2b8f] text-white rounded-[10px] font-medium px-6 py-2.5"
        >
          <Plus size={20} className="mr-2" />
          Agregar Plan
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-500">Cargando...</div>
        </div>
      ) : plans.length === 0 ? (
        <Card className="bg-white border border-[#e0e0e0] rounded-[10px] shadow-sm p-12 text-center">
          <p className="text-slate-500 mb-4">No hay planes configurados</p>
          <Button onClick={handleAdd} className="bg-[#2537b0] hover:bg-[#1e2b8f] text-white">
            <Plus size={20} className="mr-2" />
            Agregar Primer Plan
          </Button>
        </Card>
      ) : (
        <div className="space-y-8">
          {Object.keys(plansByInsurer).sort().map((insurer) => (
            <Card key={insurer} className="bg-white border border-[#e0e0e0] rounded-[10px] shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {insurer}
              </h2>
              <div className="space-y-2">
                {plansByInsurer[insurer].map((plan) => (
                  <div
                    key={plan.id}
                    data-testid={`plan-item-${plan.id}`}
                    className="flex items-center justify-between p-3 bg-slate-50 border border-[#e0e0e0] rounded-[10px] hover:border-[#2537b0]/50 transition-colors"
                  >
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900">{plan.name}</p>
                      <p className="text-xs text-slate-500">Tipo: {plan.policy_type}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        data-testid={`edit-plan-${plan.id}`}
                        onClick={() => handleEdit(plan)}
                        variant="ghost"
                        size="sm"
                        className="text-[#2537b0] hover:bg-blue-50"
                      >
                        <Edit2 size={16} />
                      </Button>
                      <Button
                        data-testid={`delete-plan-${plan.id}`}
                        onClick={() => handleDelete(plan.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingPlan ? 'Editar Plan' : 'Agregar Plan'}
            </DialogTitle>
            <DialogDescription>
              Los planes se asocian a una aseguradora y tipo de póliza específicos
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="plan-name" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Nombre del Plan*
              </Label>
              <Input
                id="plan-name"
                data-testid="plan-name-input"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ej: Segubeca, Premier, etc."
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="plan-insurer" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Aseguradora*
              </Label>
              <Select 
                value={formData.insurer} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, insurer: value }))}
              >
                <SelectTrigger data-testid="plan-insurer-select" className="mt-1">
                  <SelectValue placeholder="Selecciona aseguradora..." />
                </SelectTrigger>
                <SelectContent>
                  {catalogs.insurers.map((item) => (
                    <SelectItem key={item.id} value={item.name}>{item.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="plan-type" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Tipo de Póliza*
              </Label>
              <Select 
                value={formData.policy_type} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, policy_type: value }))}
              >
                <SelectTrigger data-testid="plan-type-select" className="mt-1">
                  <SelectValue placeholder="Selecciona tipo..." />
                </SelectTrigger>
                <SelectContent>
                  {catalogs.policy_types.map((item) => (
                    <SelectItem key={item.id} value={item.name}>{item.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              data-testid="save-plan-button"
              onClick={handleSave}
              className="bg-[#2537b0] hover:bg-[#1e2b8f] text-white"
            >
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
